import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { composeBadgeWide } from '../../components/images/composeBadgeWide';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* Bravos member badge (/member-badge) — a LANDSCAPE 1200x627 card saying "proud member of this
   community", built for LinkedIn. The member's own photo is composited into the community's
   frame; every other word lives in the frame artwork (Machina Content > Visuals).

   The share text and the member's referral link travel together: LinkedIn takes an image and
   text, so the page hands over both. Desktop LinkedIn can't be handed a file by any share
   intent, so the honest flow is spelled out as three steps — download, copy, open — with the
   mobile share sheet offered on top when the browser supports sharing files.

   Backends: GET /api/bravos/my-badge (images + brand) and the existing GET /api/refer (the
   same tracked link the Refer-a-friend page mints, so a badge share counts like any referral). */
const LINKEDIN_URL = 'https://www.linkedin.com/feed/';

const MemberBadge = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [shareLink, setShareLink] = useState('');
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | nophoto | error
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The referral link is a nice-to-have: a missing one must not cost the member their badge.
        const [badge, refer] = await Promise.all([
          apiRequest('GET', '/bravos/my-badge'),
          apiRequest('GET', '/refer').catch(() => null),
        ]);
        if (cancelled) return;
        if (badge && badge.redirect) { navigate(badge.redirect); return; }
        setData(badge);
        setShareLink((refer && refer.share_link) || '');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const community = data?.community_name || '';
  const shareText = useMemo(
    () => (data ? t('bravosBadgeShareText', { community }) : ''),
    [data, community, t]
  );

  useEffect(() => {
    if (!data) return undefined;
    if (!data.has_photo) { setStatus('nophoto'); return undefined; }
    if (!data.photo_data_uri) { setStatus('error'); return undefined; }

    let url = null;
    let cancelled = false;
    composeBadgeWide({
      photoDataUri: data.photo_data_uri,
      frameDataUri: data.frame_data_uri,
      logoDataUri: data.logo_data_uri,
      fullName: data.full_name,
      headline: t('bravosBadgeHeadline', { community }),
      primaryColor: data.primary_color,
      secondaryColor: data.secondary_color,
    })
      .then((b) => {
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setBlob(b);
        setPreviewUrl(url);
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });

    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [data, community, t]);

  const fileName = useMemo(() => {
    const base = (community || 'badge').replace(/[^\w-]+/g, '_').slice(0, 40) || 'badge';
    return `${base}-member-badge.png`;
  }, [community]);

  const fullText = shareLink ? `${shareText}\n${shareLink}` : shareText;

  const download = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const copyText = () => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2200); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullText).then(done).catch(done);
    } else {
      window.prompt(t('copyLink'), fullText);
    }
  };

  // Mobile share sheet: hands over the PNG and the text in one go. Desktop falls back to the
  // three steps below, which is all LinkedIn actually supports there.
  const share = async () => {
    if (!blob) return;
    const file = new File([blob], fileName, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: t('bravosBadgeTitle'), text: fullText });
        return;
      } catch (e) { /* cancelled or unsupported — fall through */ }
    }
    download();
  };

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div>
            <Title my="0" className="mt-1">{t('bravosBadgeTitle')}</Title>
            <p className="bravos-brand__name">{t('bravosBadgeNote')}</p>
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={() => navigate('/member-home')}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {status === 'nophoto' && (
        <Card>
          <p className="bravos-badge__prompt">{t('badgeAddPhotoTitle')}</p>
          <Link className="bravos-submit" to="/edit-profile">{t('badgeAddPhotoCta')}</Link>
        </Card>
      )}

      {status === 'error' && (
        <Card><p className="bravos-badge__prompt">{t('badgeError')}</p></Card>
      )}

      {status === 'loading' && (
        <Card>
          <div className="bravos-badge__skel" />
          <p className="bravos-badge__prompt">{t('badgeGenerating')}</p>
        </Card>
      )}

      {status === 'ready' && (
        <>
          <Card>
            <img className="bravos-badge__preview" src={previewUrl} alt={t('bravosBadgeTitle')} />
            <p className="bravos-badge__hint">{t('badgeSaveHint')}</p>
          </Card>

          <Card>
            <div className="bravos-badge__label">{t('bravosBadgeTextLabel')}</div>
            <textarea className="bravos-badge__text" readOnly rows={9} value={fullText}
                      onFocus={(e) => e.target.select()} />

            <div className="bravos-badge__steps">
              <button type="button" className="bravos-event__act bravos-badge__step" onClick={download}>
                <Icon name="download" className="me-2" />{t('bravosBadgeStep1')}
              </button>
              <button type="button" className="bravos-event__act bravos-badge__step" onClick={copyText}>
                <Icon name="copy" className="me-2" />
                {copied ? t('copied') : t('bravosBadgeStep2')}
              </button>
              <a className="bravos-event__act bravos-badge__step bravos-badge__step--go"
                 href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                <Icon name="linkedin" pack="brands" className="me-2" />{t('bravosBadgeStep3')}
              </a>
            </div>

            <button type="button" className="bravos-submit mt-3" onClick={share}>
              <Icon name="share-square" className="me-2" />{t('share')}
            </button>
          </Card>
        </>
      )}
    </Base>
  );
};

export default MemberBadge;
